import { motion } from "framer-motion";
import { UserCheck, ShieldCheck, KeyRound } from "lucide-react";

const PILLARS = [
    {
        icon: UserCheck,
        tag: "Pillar 01",
        title: "Real-Time Identity Matching",
        body: "We don't guess. Dendros cross-references your Salesforce leads against real-time employment signals to confirm every human is still in their seat — and flags the ones who aren't.",
        bullets: [
            "Job-change detection",
            "Title & company diffing",
            "Stale-tenure triggers",
        ],
    },
    {
        icon: ShieldCheck,
        tag: "Pillar 02",
        title: "The 'Purge' Safety Valve",
        body: "You stay in control. Review a clean dashboard of Ghost Leads, spot-check the evidence, and bulk-approve the cleanup in a single click before anything is written back to Salesforce.",
        bullets: [
            "Human-in-the-loop review",
            "Bulk approve / restore",
            "Full audit trail",
        ],
    },
    {
        icon: KeyRound,
        tag: "Pillar 03",
        title: "BYOK · Zero-Burn Infrastructure",
        body: "No six-figure database license. Connect your existing enrichment providers and Dendros handles the logic, orchestration, and Salesforce write-back — lean by design.",
        bullets: [
            "Bring your own keys",
            "No per-seat markup",
            "Salesforce OAuth native",
        ],
    },
];

export default function Solution() {
    return (
        <section
            id="solution"
            data-testid="solution-section"
            className="relative py-28 lg:py-36 border-t border-white/5"
        >
            <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-[#10B981]/30 to-transparent" />

            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
                    <div className="max-w-2xl">
                        <div className="text-[11px] uppercase tracking-[0.25em] font-mono text-[#10B981] font-bold">
                            The Platform
                        </div>
                        <h2 className="mt-4 font-display text-[36px] sm:text-[44px] lg:text-[56px] font-semibold leading-[1.05] tracking-[-0.025em]">
                            A cleanroom for your{" "}
                            <span className="text-volt-gradient">
                                revenue pipeline.
                            </span>
                        </h2>
                    </div>
                    <p className="text-[15px] text-neutral-400 max-w-md">
                        Three pillars. One outcome: every contact in your
                        Salesforce is a real, reachable human.
                    </p>
                </div>

                <div className="mt-14 grid md:grid-cols-3 gap-5">
                    {PILLARS.map((p, i) => (
                        <motion.article
                            key={p.title}
                            initial={{ opacity: 0, y: 28 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55, delay: i * 0.1 }}
                            data-testid={`solution-pillar-${i}`}
                            className="group relative rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-7 hover:border-[#10B981]/40 transition-all duration-300 hover:-translate-y-0.5"
                            style={{
                                boxShadow:
                                    "inset 0 1px 0 rgba(255,255,255,0.03)",
                            }}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-mono tracking-[0.25em] text-neutral-500 uppercase">
                                    {p.tag}
                                </span>
                                <div className="h-10 w-10 rounded-lg bg-[#10B981]/10 border border-[#10B981]/25 flex items-center justify-center text-[#10B981] group-hover:bg-[#10B981]/15 transition-colors">
                                    <p.icon size={18} strokeWidth={1.75} />
                                </div>
                            </div>

                            <h3 className="mt-8 font-display text-[22px] font-medium leading-snug tracking-tight">
                                {p.title}
                            </h3>
                            <p className="mt-3 text-[14px] leading-relaxed text-neutral-400">
                                {p.body}
                            </p>

                            <ul className="mt-6 space-y-2">
                                {p.bullets.map((b) => (
                                    <li
                                        key={b}
                                        className="flex items-center gap-2 text-[13px] text-neutral-300"
                                    >
                                        <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            className="text-[#10B981] flex-shrink-0"
                                        >
                                            <path
                                                fill="currentColor"
                                                d="M6 0a6 6 0 100 12A6 6 0 006 0zm-.8 8.7L2.7 6.2l.9-.9 1.6 1.6 3.2-3.2.9.9L5.2 8.7z"
                                            />
                                        </svg>
                                        {b}
                                    </li>
                                ))}
                            </ul>

                            <div
                                className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                style={{
                                    background:
                                        "radial-gradient(600px circle at var(--mx, 50%) var(--my, 0%), rgba(16,185,129,0.06), transparent 40%)",
                                }}
                            />
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}
